import type { Unit } from "../quantity";

/**
 * The inventory screens' client for `/api/inventory` (contracts/inventory-api.md).
 *
 * BACK-OFFICE WORK NEEDS THE CONNECTION, AND SAYS SO. Unlike a sale, nothing
 * here queues offline: recipes and ingredients are authored against the server
 * of record, and wastage, deliveries and counts post directly with their own
 * `opId` so a retried tap records once. A failed request surfaces the server's
 * own message — they are written for people — rather than a status code.
 */

export type IngredientState = "ok" | "low" | "negative" | "uncounted";

export interface Ingredient {
  id: string;
  name: string;
  unit: Unit;
  currentStock: number;
  lowStockThreshold: number | null;
  lastUnitCost: number | null;
  countedAt: string | null;
  active: boolean;
  state: IngredientState;
}

export interface RecipeLine {
  ingredientId: string;
  quantity: number;
}

export interface Recipe {
  id: string;
  menuItemId: string;
  variantId: string | null;
  modifierId: string | null;
  lines: RecipeLine[];
}

export interface UnmappedItem {
  menuItemId: string;
  itemName: string;
  quantitySold: number;
  lastSoldAt: string;
}

export type MovementCause = "sale" | "sale_reversal" | "wastage" | "receiving" | "count_adjustment";

export interface Movement {
  id: string;
  ingredientId: string;
  ingredientName: string;
  unit: Unit;
  quantityDelta: number;
  cause: MovementCause;
  saleId: string | null;
  unitCost: number | null;
  reason: string | null;
  staffId: string;
  staffName: string | null;
  createdAt: string;
}

export interface Reconciliation {
  ingredients: Array<{
    id: string;
    name: string;
    unit: Unit;
    currentStock: number;
    ledgerSum: number;
    difference: number;
  }>;
  balanced: boolean;
  checkedAt: string;
}

export interface Alerts {
  attention: Ingredient[];
  unmapped: UnmappedItem[];
}

export class InventoryRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null,
  ) {
    super(message);
    this.name = "InventoryRequestError";
  }
}

export function inventoryApi(token: string) {
  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    let response: Response;
    try {
      response = await fetch(`/api/inventory${path}`, {
        method,
        headers: {
          authorization: `Bearer ${token}`,
          ...(body === undefined ? {} : { "content-type": "application/json" }),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch {
      throw new InventoryRequestError(
        "No connection. Stock changes need the internet — try again once it is back.",
        0,
        null,
      );
    }

    if (!response.ok) {
      let message = `That did not go through (${response.status}).`;
      let code: string | null = null;
      try {
        const parsed = (await response.json()) as { error?: { message?: string; code?: string } };
        message = parsed.error?.message ?? message;
        code = parsed.error?.code ?? null;
      } catch {
        // Not JSON — keep the generic sentence.
      }
      throw new InventoryRequestError(message, response.status, code);
    }

    return (await response.json()) as T;
  }

  return {
    alerts: () => request<Alerts>("GET", "/alerts"),

    ingredients: (includeRetired = false) =>
      request<{ ingredients: Ingredient[] }>("GET", `/ingredients${includeRetired ? "?active=all" : ""}`),
    createIngredient: (body: { id: string; name: string; unit: Unit; lowStockThreshold: number | null }) =>
      request<{ ingredient: Ingredient }>("POST", "/ingredients", body),
    updateIngredient: (
      id: string,
      body: Partial<{ name: string; unit: Unit; lowStockThreshold: number | null; active: boolean }>,
    ) => request<{ ingredient: Ingredient }>("PATCH", `/ingredients/${id}`, body),
    retireIngredient: (id: string) => request<{ ingredient: Ingredient }>("DELETE", `/ingredients/${id}`),

    recipes: (menuItemId?: string) =>
      request<{ recipes: Recipe[] }>(
        "GET",
        `/recipes${menuItemId ? `?menuItemId=${encodeURIComponent(menuItemId)}` : ""}`,
      ),
    saveRecipe: (body: {
      menuItemId: string;
      variantId: string | null;
      modifierId: string | null;
      lines: RecipeLine[];
      applyToSameNamedModifiers?: boolean;
    }) => request<{ recipes: Recipe[] }>("PUT", "/recipes", body),
    deleteRecipe: (id: string) => request<{ deleted: string }>("DELETE", `/recipes/${id}`),

    wastage: (body: { opId: string; ingredientId: string; quantity: number; reason: string }) =>
      request<{ duplicate: boolean }>("POST", "/wastage", body),
    receiving: (body: { opId: string; ingredientId: string; quantity: number; unitCost: number | null }) =>
      request<{ duplicate: boolean }>("POST", "/receiving", body),
    count: (body: { opId: string; ingredientId: string; countedQuantity: number; reason: string }) =>
      request<{ duplicate: boolean; variance: number }>("POST", "/count", body),

    movements: (ingredientId: string, limit = 8) =>
      request<{ movements: Movement[] }>(
        "GET",
        `/movements?ingredientId=${encodeURIComponent(ingredientId)}&limit=${limit}`,
      ),
    reconciliation: () => request<Reconciliation>("GET", "/reconciliation"),
  };
}

export type InventoryApi = ReturnType<typeof inventoryApi>;
